import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ClipboardCheck, CalendarDays, Gauge, Scale, Search } from "lucide-react";
import { OverallScore, ScoreBars } from "@/components/Scores";
import { IdeaOfTheDay } from "@/components/IdeaOfTheDay";
import { TopShared } from "@/components/TopShared";

const SAMPLE = {
  nome: "Menu AI per ristoranti di quartiere",
  categoria: "SaaS",
  punteggi: { redditivita: 8, facilita: 7, concorrenza: 6, scalabilita: 9, rischio: 7 },
};

const STEPS = [
  { n: "01", t: "Racconta chi sei (o salta)", d: "Budget, tempo, interessi, online o offline, rischio. Se salti, l'AI punta dritta alle idee più redditizie." },
  { n: "02", t: "Ricevi 3 idee con punteggi", d: "Ogni idea ha cliente ideale, modello di guadagno, costi, tempi e un voto onesto su 5 criteri." },
  { n: "03", t: "Segui il piano a 30 giorni", d: "Azioni settimana per settimana, strumenti da usare e come trovare i primi 10 clienti." },
];

const FEATURES = [
  { icon: Sparkles, t: "Generatore su misura", d: "Idee concrete, monetizzabili nel 2026, pensate per il tuo profilo." },
  { icon: Gauge, t: "Valutazione AI", d: "Redditività, facilità, concorrenza, scalabilità e rischio, da 1 a 10." },
  { icon: Search, t: "Verifica la tua idea", d: "Hai già un'idea? L'AI la giudica con gli stessi criteri e propone pivot." },
  { icon: Scale, t: "Confronto affiancato", d: "Salva le migliori e mettile a confronto per scegliere quella giusta." },
];

const NICHES = ["Tecnologia", "Fitness", "Finanza personale", "Food", "Educazione", "Animali", "Turismo", "Moda"];

const Hero = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
    <div className="lg:col-span-7">
      <div className="overline-label fade-up">Fucina di idee di business con l'AI</div>
      <h1 className="fade-up font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mt-5" style={{ animationDelay: "80ms" }}>
        Trova l'idea che <span className="text-amber-400">ti fa guadagnare</span>, non solo quella che suona bene.
      </h1>
      <p className="fade-up text-base md:text-lg text-slate-400 mt-6 max-w-xl leading-relaxed" style={{ animationDelay: "160ms" }}>
        IdeaForge genera idee di business su misura, le valuta con punteggi onesti e ti consegna un piano d'azione per i primi 30 giorni.
      </p>
      <div className="fade-up flex flex-wrap gap-3 mt-9" style={{ animationDelay: "240ms" }}>
        <Link to="/genera" data-testid="hero-generate-btn"
          className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 font-semibold text-slate-950 transition-colors duration-200 hover:bg-amber-400">
          Genera le mie 3 idee <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
        <Link to="/verifica" data-testid="hero-validate-btn"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-semibold text-slate-200 transition-colors duration-200 hover:border-amber-500/60 hover:text-amber-300">
          <ClipboardCheck className="w-4 h-4" /> Verifica la tua idea
        </Link>
      </div>
    </div>
    <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "300ms" }}>
      <img src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?crop=entropy&cs=srgb&fm=jpg&q=70&w=900" alt="Scrivania con laptop e taccuino"
        className="rounded-3xl h-[420px] w-full object-cover opacity-40 border border-white/5" />
      <div className="absolute -bottom-8 -left-4 sm:left-[-2rem] right-4 sm:right-8 rounded-2xl backdrop-blur-xl bg-[#12141F]/85 border border-white/10 p-6 shadow-2xl shadow-black/50" data-testid="hero-sample-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-300">Esempio · {SAMPLE.categoria}</div>
            <div className="font-display text-lg font-bold mt-1.5">{SAMPLE.nome}</div>
          </div>
          <OverallScore value={7.6} testId="hero-sample-score" />
        </div>
        <div className="mt-4"><ScoreBars scores={SAMPLE.punteggi} testPrefix="hero-sample" /></div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <div className="overline-label">Come funziona</div>
    <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3 max-w-xl">Dall'idea al primo cliente in tre passi.</h2>
    <div className="grid md:grid-cols-3 gap-px mt-12 bg-white/5 rounded-2xl overflow-hidden border border-white/5">
      {STEPS.map((s) => (
        <div key={s.n} className="bg-[#0d0f17] p-8" data-testid={`how-step-${s.n}`}>
          <div className="font-mono text-amber-400 text-sm">{s.n}</div>
          <div className="font-display text-lg font-semibold mt-4">{s.t}</div>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Features = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
    <div className="lg:col-span-4">
      <div className="overline-label">Cosa ottieni</div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3">Non un elenco di idee. Una decisione.</h2>
      <p className="text-slate-400 mt-4 leading-relaxed">Ogni idea arriva con numeri, rischi e un percorso pratico, così puoi scegliere con la testa e partire subito.</p>
    </div>
    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
      {FEATURES.map(({ icon: Icon, t, d }) => (
        <div key={t} className="rounded-2xl bg-[#12141F] border border-white/[0.07] p-6 transition-colors duration-300 hover:border-amber-500/30">
          <Icon className="w-5 h-5 text-amber-400" />
          <div className="font-display font-semibold mt-4">{t}</div>
          <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Niches = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent p-8 sm:p-12">
      <div className="flex items-center gap-2 overline-label"><CalendarDays className="w-4 h-4" /> Parti da una passione</div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3">Scegli un interesse e lascia lavorare l'AI.</h2>
      <div className="flex flex-wrap gap-2.5 mt-8">
        {NICHES.map((n) => (
          <Link key={n} to={`/genera?interessi=${encodeURIComponent(n)}`} data-testid={`niche-${n.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-slate-200 transition-colors duration-200 hover:bg-amber-500 hover:text-slate-950 hover:border-amber-500">
            {n}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <IdeaOfTheDay />
      <TopShared />
      <HowItWorks />
      <Features />
      <Niches />
    </div>
  );
}
